<script lang="ts" setup>
import { SancareDeprecatedCheckbox, SancareOcticon } from '@sancare/typescript-commons'
import numeral from 'numeral'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { StayListUrlParams } from '@/saved-searches/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()
const router = useRouter()

const HOME_THUMBNAIL_MAX_LABEL_LENGTH = 64
const HOME_THUMBNAIL_MAX_COLOR = 9
const HOME_THUMBNAIL_DEFAULT_COLOR = 0

const props = withDefaults(defineProps<{
  name?: string
  canSelect?: boolean
  canEdit?: boolean
  canRemove?: boolean
  count?: number | string
  label?: string | null
  footer?: string
  color?: string | number
  clickable?: boolean
  selected?:boolean
  disabled?:boolean
  stayListQuery?: StayListUrlParams | null
  staylistLabel?: string | null
  pmsiType?: PmsiTypeEnum
  icon?: string | null
  loading?: boolean
}>(), {
  name: '',
  canSelect: false,
  canEdit: false,
  canRemove: false,
  count: 0,
  label: null,
  footer: '',
  color: 'uniform',
  clickable: true,
  selected: false,
  disabled: false,
  stayListQuery: null,
  staylistLabel: null,
  pmsiType: PmsiTypeEnum.MCO,
  icon: null,
  loading: false,
})

const emit = defineEmits<{
  (e: 'select'): void
}>()

const headerColor = computed(() => {
  if (props.disabled) {
    return 'disabled'
  }
  if (typeof(props.color) === 'string') {
    return props.color
  }
  if (props.color === -1) {
    return HOME_THUMBNAIL_DEFAULT_COLOR
  }
  return props.color % HOME_THUMBNAIL_MAX_COLOR
})
const displayedCount = computed(() => {
  if (typeof(props.count) === 'string') {
    return props.count
  }
  return numeral(props.count).format(props.count >= 1000 ? '0.0a' : '0a').toUpperCase()
})
const displayedLabel = computed(() => {
  if (props.label?.length > HOME_THUMBNAIL_MAX_LABEL_LENGTH) {
    return props.label.substring(0, HOME_THUMBNAIL_MAX_LABEL_LENGTH)
  }
  return props.label
})

function select() {
  if (!props.disabled) {
    emit('select')
  }
}

function consultStayList() {
  if (!props.clickable) {
    return
  }
  store.commit('resetStoredCriteria')

  if (props.stayListQuery) {
    const newRoute = {
      name: 'staylist',
      query: { ...props.stayListQuery, staylistLabel: props.staylistLabel ?? props.label },
      params: { pmsiType: props.pmsiType },
    }

    router.push(newRoute)
  }
}
</script>

<template>
  <div :class="`home-thumbnail home-thumbnail-color-${headerColor}`">
    <div
      class="g-0 align-items-center home-thumbnail-header"
      :name="`${name}_header`"
      @click="consultStayList()"
    >
      <div class="row align-items-center px-2">
        <div class="col-auto text-center home-thumbnail-count">
          <span
            v-if="loading"
            class="loader loader-xs"
          />
          <template v-else>
            {{ displayedCount }}
          </template>
        </div>
        <div
          v-if="label"
          :title="label"
          class="col text-end home-thumbnail-label"
        >
          <sancare-octicon
            v-if="icon"
            :name="icon"
            :width="15"
            :height="15"
            class="me-1"
          />
          {{ displayedLabel }}
        </div>
        <slot name="info" />
      </div>
    </div>
    <div class="g-0 align-items-center home-thumbnail-footer">
      <div class="col-2 mt-2 ms-1">
        <sancare-deprecated-checkbox
          v-if="canSelect"
          :checked="selected"
          @change="select"
        />
      </div>
      <div class="col">
        {{ footer }}
      </div>
    </div>
  </div>
</template>
